import type {
  BulkEquipmentUpdateDto,
  CreateEquipmentDto,
  EquipmentIdsDto,
  InspectEquipmentDto,
  InspectEquipmentsDto,
  UpdateEquipmentDto,
} from '@geartracker/dto';
import type {
  Equipment,
  EquipmentListQuery,
  EquipmentListResponse,
  EquipmentLookupResponse,
  EquipmentWithHistory,
} from '@geartracker/types';

import { useSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales';
import axios, { endpoints } from 'src/utils/axios';
import { getErrorMessage } from 'src/utils/error-message';
import { eventKeys } from './events.api';
import { folderKeys } from './folders.api';
import { statsKeys } from './stats.api';

import type { UseQueryOptions } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const equipmentKeys = {
  all: ['equipments'] as const,
  lists: () => [...equipmentKeys.all, 'list'] as const,
  list: (type: string, id?: string | number) =>
    [...equipmentKeys.lists(), `${type}${id ? `_${id}` : ''}`] as const,
  paginatedLists: () => [...equipmentKeys.all, 'paginated-list'] as const,
  paginatedList: (params: EquipmentListQuery) =>
    [...equipmentKeys.paginatedLists(), params] as const,
  details: () => [...equipmentKeys.all, 'detail'] as const,
  detail: (id: number) => [...equipmentKeys.details(), id] as const,
  detailIds: (ids: number[]) => [...equipmentKeys.lists(), ids] as const, // acts as a list for keys
};

const serializeEquipmentListQuery = (query: EquipmentListQuery = {}) => ({
  ...query,
  folderIds: query.folderIds?.join(','),
  userIds: query.userIds?.join(','),
  tagIds: query.tagIds?.join(','),
  generalStatuses: query.generalStatuses?.join(','),
  equipmentIds: query.equipmentIds?.join(','),
});

const invalidatePaginatedEquipmentLists = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  return queryClient.invalidateQueries({
    queryKey: equipmentKeys.paginatedLists(),
  });
};

export const getEquipment = async (
  equipmentId: number,
): Promise<EquipmentWithHistory> => {
  const response = await axios.get(endpoints.equipment.root(equipmentId));
  return response.data;
};

export const getEquipments = async (
  folderIds?: number[],
  userId?: number,
): Promise<Equipment[]> => {
  const response = await axios.get(endpoints.equipment.root(), {
    params: {
      ...(folderIds && folderIds.length > 0
        ? { folderIds: folderIds.join(',') }
        : {}),
      userId,
    },
  });
  return response.data;
};

export const getEquipmentList = async (
  query: EquipmentListQuery,
): Promise<EquipmentListResponse> => {
  const response = await axios.get(endpoints.equipment.list, {
    params: serializeEquipmentListQuery(query),
  });
  return response.data;
};

export const lookupEquipment = async (
  search: string,
): Promise<EquipmentLookupResponse> => {
  const response = await axios.get(endpoints.equipment.lookup, {
    params: { search },
  });
  return response.data;
};

export const getEquipmentsWithHistory = async (options?: {
  includeChecklist?: boolean;
}): Promise<EquipmentWithHistory[]> => {
  const response = await axios.get(endpoints.equipment.histories, {
    params: options?.includeChecklist ? { includeChecklist: true } : undefined,
  });
  return response.data;
};

//Get equipment with history of specific ids
export const getEquipmentsIdsWithHistory = async (
  equipmentIds: EquipmentIdsDto,
  options?: { includeChecklist?: boolean },
): Promise<EquipmentWithHistory[]> => {
  const response = await axios.post(
    endpoints.equipment.histories,
    equipmentIds,
    {
      params: options?.includeChecklist
        ? { includeChecklist: true }
        : undefined,
    },
  );
  return response.data;
};

export const createEquipment = async (
  equipment: CreateEquipmentDto,
): Promise<EquipmentWithHistory> => {
  const response = await axios.post(endpoints.equipment.root(), equipment);
  return response.data;
};

export const updateEquipment = async ({
  equipmentId,
  equipment,
}: {
  equipmentId: number;
  equipment: UpdateEquipmentDto;
}): Promise<EquipmentWithHistory> => {
  const response = await axios.patch(
    endpoints.equipment.root(equipmentId),
    equipment,
  );
  return response.data;
};

export const inspectEquipment = async ({
  equipmentId,
  inspectionPayload,
}: {
  equipmentId: number;
  inspectionPayload: InspectEquipmentDto;
}): Promise<EquipmentWithHistory> => {
  const response = await axios.post(
    endpoints.equipment.inspect.root(equipmentId),
    inspectionPayload,
  );
  return response.data;
};

export const inspectEquipments = async (
  inspectionPayload: InspectEquipmentsDto,
): Promise<EquipmentWithHistory[]> => {
  const response = await axios.post(
    endpoints.equipment.inspect.multiple,
    inspectionPayload,
  );
  return response.data;
};

export const removeEquipment = async (
  equipmentId: number,
): Promise<Equipment> => {
  const response = await axios.delete(endpoints.equipment.root(equipmentId));
  return response.data;
};

//allocate equipment to user
export const allocateEquipment = async ({
  equipmentId,
  userId,
}: {
  equipmentId: number;
  userId: number | null;
}): Promise<EquipmentWithHistory> => {
  const response = await axios.post(endpoints.equipment.allocate(equipmentId), {
    userId,
  });
  return response.data;
};

/*
  HOOKS
*/

export const useEquipment = (id: number) => {
  return useQuery({
    queryKey: equipmentKeys.detail(id),
    queryFn: () => getEquipment(id),
    enabled: !!id,
  });
};

export const useEquipments = ({
  disabled,
  userId,
  folderIds,
}: {
  disabled?: boolean;
  userId?: number;
  folderIds?: number[];
}) => {
  const folderKey = folderIds?.length ? folderIds.join(',') : undefined;

  return useQuery({
    queryKey: equipmentKeys.list(
      folderKey ? 'folders' : userId ? 'user' : 'all',
      folderKey || userId,
    ),
    queryFn: () => getEquipments(folderIds, userId),
    enabled: !disabled,
  });
};

export const useEquipmentList = (
  query: EquipmentListQuery,
  options?: { disabled?: boolean },
) => {
  return useQuery({
    queryKey: equipmentKeys.paginatedList(query),
    queryFn: () => getEquipmentList(query),
    enabled: !options?.disabled,
  });
};

export const useInfiniteEquipmentList = (
  query: Omit<EquipmentListQuery, 'page'>,
  options?: { disabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: equipmentKeys.paginatedList({ ...query, page: undefined }),
    queryFn: ({ pageParam = 0 }) =>
      getEquipmentList({ ...query, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.page + 1) * lastPage.take;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: !options?.disabled,
  });
};

/**
 * A custom hook for fetching equipment data along with their history.
 * This hook decides which query to perform based on the presence and content of the `ids` argument.
 * If `ids` is provided and contains an array of identifiers, a query is made to fetch the history
 * of specific equipment. Otherwise, a query is made to fetch the history of all equipment.
 */
export const useEquipmentsIdsWithHistory = (
  ids?: EquipmentIdsDto,
  options?: { includeChecklist?: boolean; enabled?: boolean },
) => {
  type QueryConfig = UseQueryOptions<
    EquipmentWithHistory[],
    unknown,
    EquipmentWithHistory[],
    // biome-ignore lint/suspicious/noExplicitAny: reason
    any
  >;

  const isEnabled = options?.enabled !== false;

  const query: QueryConfig =
    ids && ids.length > 0
      ? {
          queryKey: [...equipmentKeys.detailIds(ids), options],
          queryFn: () => getEquipmentsIdsWithHistory(ids, options),
          enabled: isEnabled,
        }
      : {
          queryKey: [...equipmentKeys.list('history'), options],
          queryFn: () => getEquipmentsWithHistory(options),
          enabled: isEnabled,
        };

  return useQuery(query);
};

export const useCreateEquipment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: createEquipment,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.create.success'));
      queryClient.setQueryData(equipmentKeys.detail(data.id), data);
      //ALL
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        // we add as first so desc order is kept
        (previousData: Equipment[]) =>
          previousData ? [data, ...previousData] : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);

      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

export const useUpdateEquipment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: updateEquipment,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.update.success'));
      queryClient.setQueryData(equipmentKeys.detail(data.id), data);

      //LISTS
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        (previousData: Equipment[]) =>
          previousData
            ? previousData.map((equipment) =>
                equipment.id === data.id ? data : equipment,
              )
            : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);

      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

export const useInspectEquipment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: inspectEquipment,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.inspect.success'));
      queryClient.setQueryData(equipmentKeys.detail(data.id), data);
      //ALL
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        (previousData: Equipment[]) =>
          previousData
            ? previousData.map((equipment) =>
                equipment.id === data.id ? data : equipment,
              )
            : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);

      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

export const useInspectEquipments = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: inspectEquipments,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.inspect.success'));
      for (const equipment of data) {
        queryClient.setQueryData(equipmentKeys.detail(equipment.id), equipment);
      }
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        (previousData: Equipment[]) =>
          previousData
            ? previousData.map(
                (equipment) =>
                  data.find((e) => e.id === equipment.id) || equipment,
              )
            : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

export const useRemoveEquipment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: removeEquipment,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.remove.success'));
      queryClient.removeQueries({ queryKey: equipmentKeys.detail(data.id) });
      //ALL LISTS
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        (previousData: Equipment[]) =>
          previousData
            ? previousData.filter((equipment) => equipment.id !== data.id)
            : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);

      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

// Custom hook to allocate equipment to a specific user
export const useAllocateEquipment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: allocateEquipment,
    onSuccess: (data) => {
      enqueueSnackbar(t('api.equipment.allocate.success'));
      queryClient.setQueryData(equipmentKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
      //ALL
      queryClient.setQueriesData(
        { queryKey: equipmentKeys.lists() },
        (previousData: Equipment[]) =>
          previousData
            ? previousData.map((equipment) =>
                equipment.id === data.id ? data : equipment,
              )
            : undefined,
      );
      void invalidatePaginatedEquipmentLists(queryClient);
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

const equipmentsBulkUpdate = async (
  dto: BulkEquipmentUpdateDto & { equipmentIds: number[] },
): Promise<Equipment[]> => {
  const response = await axios.patch(endpoints.equipment.bulk, dto);
  return response.data;
};

export const useEquipmentsBulkUpdate = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: equipmentsBulkUpdate,
    onSuccess: (data, dto) => {
      enqueueSnackbar(
        t('api.equipment.bulk.update.success', { count: data.length }),
      );
      if (dto.folderId) {
        void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
        void queryClient.invalidateQueries({
          queryKey: folderKeys.detail(dto.folderId),
        });
      }

      void queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      void invalidatePaginatedEquipmentLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
      for (const equipmentId of dto.equipmentIds) {
        void queryClient.invalidateQueries({
          queryKey: equipmentKeys.detail(equipmentId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};

const equipmentBulkDeleteRequest = async (
  dto: EquipmentIdsDto,
): Promise<{ count: number }> => {
  const response = await axios.delete(endpoints.equipment.bulk, {
    data: dto,
  });
  return response.data;
};

export const useEquipmentsBulkDelete = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: equipmentBulkDeleteRequest,
    onSuccess: (data, dto) => {
      enqueueSnackbar(
        t('api.equipment.bulk.delete.success', { count: data.count }),
      );

      void queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      void invalidatePaginatedEquipmentLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
      for (const equipmentId of dto) {
        void queryClient.removeQueries({
          queryKey: equipmentKeys.detail(equipmentId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};
